import { useInfiniteQuery } from '@tanstack/react-query';

import type { GetOCClaimsListRequestParams } from '@/views/screens/Claims/data-provider/over-charge-claims-list';
import { fetchOCClaimsList } from '@/views/screens/Claims/data-provider/over-charge-claims-list';
import { getFooterCountParam } from '@/views/screens/Claims/data-provider/over-charge-claims-list/normalize';

interface OCClaimsListPage {
  data: Awaited<ReturnType<typeof fetchOCClaimsList>>;
  nextPage?: number;
  pageNo: number;
}

export const useGetOCClaimsList = ({
  enabled = true,
  searchFilters,
  companyId,
}: {
  enabled?: boolean;
  searchFilters: Omit<GetOCClaimsListRequestParams, 'pageNo'>;
  companyId?: number | string | null;
}) => {
  return useInfiniteQuery<OCClaimsListPage, Error, any, any, number>({
    enabled,
    getNextPageParam: (lastPage) => lastPage?.nextPage,
    initialPageParam: 1,
    queryFn: async ({ pageParam = 1, signal }): Promise<OCClaimsListPage> => {
      const pageNo = pageParam as number;
      const data = await fetchOCClaimsList({ ...searchFilters, pageNo }, { signal });
      const footerParam = getFooterCountParam(searchFilters.FooterStatus);
      const totalCount = Number((data as any)?.[footerParam] ?? 0);
      const totalPageCount = Math.floor((totalCount + (searchFilters.pageSize - 1)) / searchFilters.pageSize);
      const nextPage = pageNo < totalPageCount ? pageNo + 1 : undefined;

      return {
        data,
        nextPage,
        pageNo,
      };
    },
    queryKey: ['claims-over-charge-list', companyId, searchFilters],
    select: (data) => {
      const flatClaims = data.pages.flatMap((page: any) => page?.data?.claimsData ?? []);
      const lastPage = data.pages[data.pages.length - 1] as any;
      const footerData = lastPage?.data ?? null;

      return {
        ...data,
        flatClaims,
        footerData,
        totalCount: Number(footerData?.totalClaim ?? 0),
      };
    },
  });
};
